'use client'

import Link from 'next/link'
import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation, faMagnifyingGlass } from '@awesome.me/kit-94e593bf23/icons/sharp/light'
import { faSpinnerThird } from '@awesome.me/kit-94e593bf23/icons/classic/light'

import searchAction from '@/app/_lib/actions/SearchAction'
import ProductListingSearchResult from '@/app/_components/ProductListingSearchResult'
import { useDebouncedValue } from '@/app/_lib/hooks/useDebounce'
import { trackSearch } from '@/app/_lib/analytics/ga4'

const MIN_SEARCH_LENGTH = 2
const RESULTS_PER_PAGE = 12

export default function SearchContent() {
    const searchParams = useSearchParams()
    const router = useRouter()
    const initialKeywords = searchParams.get('keywords') || ''

    const [mobileTab, setMobileTab] = useState('products')
    const [isPending, setIsPending] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [hasMoreResults, setHasMoreResults] = useState(false)
    const [inputValue, setInputValue] = useState(initialKeywords)
    const debouncedKeywords = useDebouncedValue(inputValue, 300)

    const [results, setResults] = useState([])
    const [numFound, setNumFound] = useState(0)
    const [designers, setDesigners] = useState([])
    const [suppliers, setSuppliers] = useState([])
    const [suggestions, setSuggestions] = useState(null)
    const [showNoResultsMessage, setShowNoResultsMessage] = useState(false)

    const inputRef = useRef(null)
    const loaderRef = useRef(null)
    const abortControllerRef = useRef(null)

    useEffect(() => {
        // Cancel any in-flight request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort()
        }

        // Require minimum characters before searching
        if (debouncedKeywords.length < MIN_SEARCH_LENGTH) {
            clearSearchResults()
            setIsPending(false)
            setShowNoResultsMessage(false)

            // Clear URL query string when search is too short
            if (debouncedKeywords.length === 0) {
                router.replace('/search', { scroll: false })
            }
            return
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController()
        let isCancelled = false

        const search = async () => {
            setIsPending(true)

            // Update URL query string
            router.replace(`/search?keywords=${encodeURIComponent(debouncedKeywords)}`, { scroll: false })

            try {
                const response = await searchAction(debouncedKeywords, 0, RESULTS_PER_PAGE)

                // Don't update state if this request was cancelled
                if (isCancelled) return

                const productsData = response.payload?.data || []
                const numFound = response.payload?.num_found || 0

                if (numFound > 0) {
                    trackSearch(debouncedKeywords)
                    setShowNoResultsMessage(false)
                    setResults(productsData)
                    setNumFound(numFound)
                    setDesigners(response.payload.designers || [])
                    setSuppliers(response.payload.suppliers || [])
                    setSuggestions(response.payload.suggestions || null)
                    setHasMoreResults(productsData.length < numFound)
                } else {
                    clearSearchResults()
                    setShowNoResultsMessage(true)
                }
            } catch (error) {
                // Ignore abort errors
                if (error.name !== 'AbortError') {
                    console.error('Search error:', error)
                }
            } finally {
                if (!isCancelled) {
                    setIsPending(false)
                }
            }
        }

        search()

        // Cleanup function to cancel request if component unmounts or keywords change
        return () => {
            isCancelled = true
            if (abortControllerRef.current) {
                abortControllerRef.current.abort()
            }
        }
    }, [debouncedKeywords, router])

    // Load more results function
    const loadMoreResults = async () => {
        if (isLoadingMore || !hasMoreResults || !debouncedKeywords) return

        setIsLoadingMore(true)
        const response = await searchAction(debouncedKeywords, results.length, RESULTS_PER_PAGE)

        const productsData = response.payload?.data || []

        if (productsData.length > 0) {
            setResults(prev => [...prev, ...productsData])
            setHasMoreResults(results.length + productsData.length < numFound)
        } else {
            setHasMoreResults(false)
        }
        setIsLoadingMore(false)
    }

    // Intersection observer for infinite scroll
    useEffect(() => {
        if (!loaderRef.current || !hasMoreResults) return

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMoreResults && !isLoadingMore) {
                    loadMoreResults()
                }
            },
            { threshold: 0.1 }
        )

        observer.observe(loaderRef.current)

        return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMoreResults, isLoadingMore])

    const clearSearchResults = () => {
        setNumFound(0)
        setResults([])
        setDesigners([])
        setSuppliers([])
        setSuggestions(null)
        setHasMoreResults(false)
    }

    const handleSearchInput = (event) => {
        setInputValue(event.target.value)
    }

    // Results update live as you type; Enter just dismisses the mobile
    // keyboard, matching the keyboard's own check mark / Done key
    const handleInputKeyDown = (event) => {
        if (event.key === 'Enter' && window.innerWidth < 1024) {
            event.target.blur()
        }
    }

    return (
        <div className="site-padding mt-6 min-h-[400px] mb-[100px]">
            <div className="max-w-[1800px] mx-auto">

                {/* Search input bar */}
                <div className="py-3 mb-2">
                    <div className="flex items-center justify-between w-full text-nowrap">
                        <div className="flex items-center justify-start w-full">
                            <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" className="text-primary text-3xl mr-2 mt-1" />
                            <input
                                ref={inputRef}
                                onChange={handleSearchInput}
                                onKeyDown={handleInputKeyDown}
                                autoComplete="off"
                                type="text"
                                name="keywords"
                                defaultValue={initialKeywords}
                                placeholder="Search"
                                aria-label="Search products"
                                className="w-full block h-[29px] pt-1 text-[16px] lg:text-md text-light font-medium caret-primary focus:ring-0 focus:outline-none inset-0 bg-transparent"
                            />
                        </div>
                    </div>
                </div>

                {/* No results message */}
                {showNoResultsMessage && (
                    <div className="flex items-center justify-start md:justify-center relative w-full bg-white text-nowrap py-4">
                        <FontAwesomeIcon icon={faCircleExclamation} aria-hidden="true" className="text-primary text-md md:text-xl mr-2" />
                        <span className="text-light text-sm"><span className="font-medium">Sorry!</span> We couldn&apos;t find any search results for &quot;{inputValue}&quot;</span>
                    </div>
                )}

                {/* Results */}
                {results.length > 0 && (
                    <div className="text-left">
                        {/* Mobile tabs - products, brands, designers */}
                        <div className="lg:hidden flex gap-6 py-3 border-b border-gray-8 mb-4">
                            <button
                                type="button"
                                onClick={() => setMobileTab('products')}
                                className={`text-xs font-medium transition-colors ${mobileTab === 'products' ? 'text-primary' : 'text-light hover:text-dark'}`}
                            >
                                Products {numFound > 0 && <span className="text-light-3">({numFound})</span>}
                            </button>
                            <button
                                type="button"
                                onClick={() => setMobileTab('brands')}
                                className={`text-xs font-medium transition-colors ${mobileTab === 'brands' ? 'text-primary' : 'text-light hover:text-dark'}`}
                            >
                                Brands {suppliers.length > 0 && <span className="text-light-3">({suppliers.length})</span>}
                            </button>
                            <button
                                type="button"
                                onClick={() => setMobileTab('designers')}
                                className={`text-xs font-medium transition-colors ${mobileTab === 'designers' ? 'text-primary' : 'text-light hover:text-dark'}`}
                            >
                                Designers {designers.length > 0 && <span className="text-light-3">({designers.length})</span>}
                            </button>
                        </div>

                        {/* Three column layout */}
                        <div className="flex flex-col lg:flex-row pt-0 lg:pt-4 pb-4 lg:pb-4">
                            {/* Brands column */}
                            <div className={`${mobileTab === 'brands' ? 'block' : 'hidden'} lg:block lg:w-[10%] lg:min-w-[140px] lg:shrink-0 pb-2 lg:pb-0`}>
                                <div className={`hidden lg:block font-medium text-xs mb-4 ${suppliers.length === 0 ? 'text-light-3' : 'text-light'}`}>
                                    Brands
                                </div>
                                <div className="grid grid-cols-2 gap-4 lg:block">
                                    {suppliers.length > 0 &&
                                        <>
                                            {suppliers.filter(s => s.url).map((supplier, index) => (
                                                <Link
                                                    key={supplier.supplier_id || index}
                                                    href={supplier.url}
                                                    className="flex flex-col items-start hover:opacity-80 lg:mb-6 transition-colors duration-200"
                                                >
                                                    {supplier.image && (
                                                        <span className="overlay-block">
                                                            <img src={supplier.image} alt={supplier.supplier_name} className="w-full lg:w-32 aspect-[8/7] object-cover" />
                                                        </span>
                                                    )}
                                                    <span className="text-2xs text-light mt-1">{supplier.supplier_name} {supplier.product_count > 0 && <span className="text-light-3">({supplier.product_count})</span>}</span>
                                                </Link>
                                            ))}
                                        </>
                                    }
                                </div>
                            </div>

                            {/* Designers column */}
                            <div className={`${mobileTab === 'designers' ? 'block' : 'hidden'} lg:block lg:w-[10%] lg:min-w-[140px] lg:shrink-0`}>
                                <div className={`hidden lg:block font-medium text-xs mb-4 ${designers.length === 0 ? 'text-light-3' : 'text-light'}`}>
                                    Designers
                                </div>
                                <div className="grid grid-cols-2 gap-4 lg:block">
                                    {designers.length > 0 &&
                                        <>
                                            {designers.filter(d => d.url).map((designer, index) => (
                                                <Link
                                                    key={designer.designer_id || index}
                                                    href={designer.url}
                                                    className="flex flex-col items-start hover:opacity-80 lg:mb-6 transition-colors duration-200"
                                                >
                                                    {designer.image && (
                                                        <span className="overlay-block">
                                                            <img src={designer.image} alt={designer.designer_name} className="w-full lg:w-32 h-auto object-contain" />
                                                        </span>
                                                    )}
                                                    <span className="text-2xs text-light mt-1">{designer.designer_name} {designer.product_count > 0 && <span className="text-light-3">({designer.product_count})</span>}</span>
                                                </Link>
                                            ))}
                                        </>
                                    }
                                </div>
                            </div>

                            {/* Products column - takes remaining space */}
                            <div className={`${mobileTab === 'products' ? 'block' : 'hidden'} lg:block lg:flex-1 lg:ml-[5%]`}>
                                <div className="hidden lg:flex font-medium text-xs mb-4 items-baseline gap-2">
                                    <span>Products</span>
                                    {numFound > 0 &&
                                        <span className="text-light font-normal"><span className="text-light-3">({numFound})</span> results for <span className="underline text-primary">{inputValue}</span></span>
                                    }
                                </div>

                                {numFound > 0 ? (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 pb-4">
                                            {results.filter(product => product.image).map((product, index) => (
                                                <div key={`${product.sub_product_id}-${index}`} className="border-b md:border-b-0 border-gray-8 mb-6">
                                                    <ProductListingSearchResult product={product} additionalClasses={''} />
                                                </div>
                                            ))}
                                        </div>
                                        {/* Infinite scroll loader */}
                                        {hasMoreResults && (
                                            <div ref={loaderRef} className="flex flex-col items-center justify-center pt-6 pb-12 text-light">
                                                {isLoadingMore ? (
                                                    <div className="flex items-center gap-2">
                                                        <FontAwesomeIcon icon={faSpinnerThird} aria-hidden="true" className="animate-spin text-primary text-lg" />
                                                        <span className="text-sm">Loading more products...</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-light-3">Scroll for more results</span>
                                                )}
                                            </div>
                                        )}
                                        {!hasMoreResults && results.length > 0 && results.length >= numFound && (
                                            <div className="text-center py-4 text-xs text-light-3">
                                                Showing all {numFound} results
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-base font-extra-light text-light col-span-6 mt-12">
                                        {isPending &&
                                            <div className="flex items-center gap-2">
                                                <FontAwesomeIcon icon={faSpinnerThird} aria-hidden="true" className="animate-spin text-primary text-lg" />
                                                <span className="text-sm">Searching...</span>
                                            </div>
                                        }
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Initial state - no search yet */}
                {!results.length && !showNoResultsMessage && !isPending && (
                    <div className="text-base font-extra-light text-light py-12">
                        Enter a search term to find products
                    </div>
                )}

                {/* Loading state */}
                {isPending && !results.length && (
                    <div className="flex items-center gap-2 py-12">
                        <FontAwesomeIcon icon={faSpinnerThird} aria-hidden="true" className="animate-spin text-primary text-lg" />
                        <span className="text-sm text-light">Searching...</span>
                    </div>
                )}
            </div>
        </div>
    )
}
